import React, { useState, useEffect, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Star,
  Plus,
  TrendingUp,
  TrendingDown,
  Users,
  Calendar,
  History,
  Settings
} from 'lucide-react';
import { useToast } from '../contexts/ToastContext';

interface Customer {
  id: number;
  name: string;
  cpf: string;
  email: string;
  total_points: number;
}

interface Transaction {
  id: number;
  customer_id: number;
  type: 'earned' | 'redeemed';
  points: number;
  description: string;
  created_at: string;
  customer: Customer;
}

interface StoreConfig {
  points_per_currency: number;
  min_purchase_amount: number | null;
  points_rounding: 'floor' | 'round' | 'ceil';
}

const Container = styled.div`
  min-height: 100vh;
  background: #f8f9fa;
  padding: 24px;

  @media (max-width: 768px) {
    padding: 16px;
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;

  @media (max-width: 768px) {
    flex-wrap: wrap;
    gap: 12px;
  }
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  background: white;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  color: #6c757d;
  font-size: 14px;
  transition: all 0.2s ease;

  &:hover {
    background: #f8f9fa;
    color: #495057;
  }
`;

const Title = styled.h1`
  font-size: 24px;
  font-weight: 600;
  color: #333;
  margin: 0;
`;

const Actions = styled.div`
  display: flex;
  gap: 12px;

  @media (max-width: 768px) {
    width: 100%;
    flex-wrap: wrap;
  }
`;

const AddButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 20px;
  background: #28a745;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  transition: background-color 0.2s ease;

  &:hover {
    background: #218838;
  }

  @media (max-width: 768px) {
    flex: 1 1 160px;
    justify-content: center;
  }
`;

const ConfigButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 20px;
  background: white;
  color: #6c757d;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.2s ease;

  &:hover {
    background: #f8f9fa;
    color: #495057;
    border-color: #adb5bd;
  }

  @media (max-width: 768px) {
    flex: 1 1 160px;
    justify-content: center;
  }
`;

const SelectInput = styled.select`
  width: 100%;
  padding: 12px;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  font-size: 14px;
  transition: border-color 0.2s ease;
  background: white;

  &:focus {
    outline: none;
    border-color: #00d4aa;
    box-shadow: 0 0 0 3px rgba(0, 212, 170, 0.1);
  }
`;

const StatsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
  margin-bottom: 24px;
`;

const StatCard = styled.div`
  background: white;
  padding: 24px;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  border: 1px solid #e9ecef;
`;

const StatIcon = styled.div<{ color?: string }>`
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: ${props => props.color || '#00d4aa'}20;
  color: ${props => props.color || '#00d4aa'};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
`;

const StatValue = styled.div`
  font-size: 24px;
  font-weight: 600;
  color: #333;
  margin-bottom: 4px;
`;

const StatLabel = styled.div`
  font-size: 14px;
  color: #6c757d;
`;

const ContentArea = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  border: 1px solid #e9ecef;
  overflow: hidden;
`;

const TabsContainer = styled.div`
  display: flex;
  border-bottom: 1px solid #e9ecef;

  @media (max-width: 768px) {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }
`;

const Tab = styled.button<{ active: boolean }>`
  padding: 16px 24px;
  background: ${props => props.active ? '#f8f9fa' : 'transparent'};
  border: none;
  border-bottom: 2px solid ${props => props.active ? '#00d4aa' : 'transparent'};
  color: ${props => props.active ? '#00d4aa' : '#6c757d'};
  font-weight: 500;
  transition: all 0.2s ease;
  cursor: pointer;

  &:hover {
    background: #f8f9fa;
    color: #00d4aa;
  }

  @media (max-width: 768px) {
    padding: 12px 16px;
    white-space: nowrap;
  }
`;

const TabContent = styled.div`
  padding: 24px;

  @media (max-width: 768px) {
    padding: 16px;
  }
`;

const TransactionList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const TransactionItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  background: #f8f9fa;
  border-radius: 8px;
  border-left: 4px solid transparent;

  &.earned {
    border-left-color: #28a745;
  }

  &.redeemed {
    border-left-color: #dc3545;
  }

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }
`;

const TransactionInfo = styled.div`
  flex: 1;
`;

const TransactionCustomer = styled.div`
  font-weight: 500;
  color: #333;
  margin-bottom: 4px;
`;

const TransactionDescription = styled.div`
  font-size: 14px;
  color: #6c757d;
  margin-bottom: 2px;
`;

const TransactionDate = styled.div`
  font-size: 12px;
  color: #6c757d;
`;

const TransactionPoints = styled.div<{ type: string }>`
  font-size: 18px;
  font-weight: 600;
  color: ${props => props.type === 'earned' ? '#28a745' : '#dc3545'};
  display: flex;
  align-items: center;
  gap: 4px;
`;

const Modal = styled.div<{ $show: boolean }>`
  display: ${props => props.$show ? 'flex' : 'none'};
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 16px;
  padding: 32px;
  max-width: 500px;
  width: 90%;
  max-height: 90vh;
  overflow-y: auto;
`;

const ModalTitle = styled.h2`
  font-size: 20px;
  font-weight: 600;
  color: #333;
  margin: 0 0 24px;
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  font-size: 14px;
  font-weight: 500;
  color: #333;
  margin-bottom: 8px;
`;

const Select = styled.select`
  width: 100%;
  padding: 12px;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  font-size: 14px;
  transition: border-color 0.2s ease;
  background: white;

  &:focus {
    outline: none;
    border-color: #00d4aa;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }
`;

const Input = styled.input`
  width: 100%;
  padding: 12px;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  font-size: 14px;
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: #00d4aa;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }
`;

const ModalActions = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 24px;
`;

const ModalButton = styled.button<{ variant?: 'primary' | 'secondary' }>`
  padding: 12px 20px;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  background: ${props => 
    props.variant === 'primary' ? '#28a745' : '#f8f9fa'
  };
  color: ${props => 
    props.variant === 'primary' ? 'white' : '#6c757d'
  };

  &:hover {
    background: ${props => 
      props.variant === 'primary' ? '#218838' : '#e9ecef'
    };
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 48px;
  color: #6c757d;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 48px;
  color: #6c757d;
`;

const InfoBanner = styled.div`
  background: #e8f5e9;
  border: 1px solid #a5d6a7;
  border-radius: 8px;
  padding: 10px 14px;
  font-size: 13px;
  color: #2e7d32;
  margin-bottom: 16px;
`;

const PointsPreview = styled.div<{ $valid: boolean }>`
  margin-top: 6px;
  font-size: 13px;
  font-weight: 600;
  color: ${props => props.$valid ? '#28a745' : '#dc3545'};
`;

const ToggleLink = styled.button`
  background: none;
  border: none;
  color: #00d4aa;
  font-size: 13px;
  text-decoration: underline;
  cursor: pointer;
  padding: 0;
  margin-top: 4px;

  &:hover {
    color: #00b894;
  }
`;

export const PointsPage: React.FC = () => {
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();
  const [activeTab, setActiveTab] = useState('transactions');
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [storeId, setStoreId] = useState<number | null>(null);
  const [storeConfig, setStoreConfig] = useState<StoreConfig>({
    points_per_currency: 1,
    min_purchase_amount: null,
    points_rounding: 'round',
  });
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [configDraft, setConfigDraft] = useState<StoreConfig>({ points_per_currency: 1, min_purchase_amount: null, points_rounding: 'round' });
  const [isSavingConfig, setIsSavingConfig] = useState(false);
  const [manualMode, setManualMode] = useState(false);
  const [formData, setFormData] = useState({
    customer_id: '',
    purchase_amount: '',
    points: '',
    description: ''
  });

  // Calcula preview dos pontos via valor de compra (igual à lógica do backend)
  const previewPoints = useMemo(() => {
    if (manualMode) return null;
    const amount = parseFloat(formData.purchase_amount);
    if (isNaN(amount) || amount <= 0) return null;
    const { points_per_currency, min_purchase_amount, points_rounding } = storeConfig;
    if (min_purchase_amount !== null && amount < min_purchase_amount) return -1; // sinaliza abaixo do mínimo
    const raw = parseFloat((amount * points_per_currency).toFixed(10));
    const pts = points_rounding === 'floor' ? Math.floor(raw) : points_rounding === 'ceil' ? Math.ceil(raw) : Math.round(raw);
    return Math.max(0, pts);
  }, [formData.purchase_amount, manualMode, storeConfig]);

  const loadTransactions = useCallback(async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch('/api/points-transactions', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
        },
      });

      const data = await response.json();
      if (data.success) {
        setTransactions(data.data);
      }
    } catch (error) {
      console.error('Erro ao carregar transações:', error);
    }
  }, []);

  const loadCustomers = useCallback(async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch('/api/customers', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
        },
      });

      const data = await response.json();
      if (data.success) {
        setCustomers(data.data);
      }
    } catch (error) {
      console.error('Erro ao carregar clientes:', error);
    }
  }, []);

  const loadStoreConfig = useCallback(async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch('/api/stores', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
        },
      });
      const data = await response.json();
      if (data.success && Array.isArray(data.data) && data.data.length > 0) {
        const store = data.data[0];
        const cfg: StoreConfig = {
          points_per_currency: store.points_per_currency ?? 1,
          min_purchase_amount: store.min_purchase_amount ?? null,
          points_rounding: store.points_rounding ?? 'round',
        };
        setStoreId(store.id);
        setStoreConfig(cfg);
      }
    } catch {
      // Mantém config padrão se falhar
    }
  }, []);

  const handleOpenConfig = () => {
    setConfigDraft({ ...storeConfig });
    setShowConfigModal(true);
  };

  const handleSaveConfig = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!storeId) return;
    setIsSavingConfig(true);
    try {
      const token = localStorage.getItem('auth_token');
      const apiUrl = process.env.REACT_APP_API_URL || '/api';
      const response = await fetch(`${apiUrl}/stores/${storeId}/points-settings`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(configDraft),
      });
      const data = await response.json();
      if (data.success) {
        setStoreConfig({ ...configDraft });
        setShowConfigModal(false);
        showSuccess('Configurações de pontos salvas!');
      } else {
        showError(data.message || 'Erro ao salvar configurações');
      }
    } catch {
      showError('Erro de conexão');
    } finally {
      setIsSavingConfig(false);
    }
  };

  const loadData = useCallback(async () => {
    try {
      await Promise.all([
        loadTransactions(),
        loadCustomers(),
        loadStoreConfig(),
      ]);
    } finally {
      setLoading(false);
    }
  }, [loadCustomers, loadTransactions, loadStoreConfig]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleAddPoints = () => {
    setFormData({ customer_id: '', purchase_amount: '', points: '', description: '' });
    setManualMode(false);
    setShowModal(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!manualMode && previewPoints === -1) {
      const min = storeConfig.min_purchase_amount;
      showError(`Valor abaixo do mínimo (R$ ${min?.toFixed(2).replace('.', ',')})`);
      return;
    }
    if (!manualMode && (previewPoints === null || previewPoints === 0)) {
      showError('Informe um valor de compra válido');
      return;
    }

    const payload: Record<string, any> = { description: formData.description };
    if (manualMode) {
      payload.points = parseInt(formData.points, 10);
    } else {
      payload.purchase_amount = parseFloat(formData.purchase_amount);
    }

    try {
      const token = localStorage.getItem('auth_token');
      const apiUrl = process.env.REACT_APP_API_URL || '/api';
      const response = await fetch(`${apiUrl}/customers/${formData.customer_id}/add-points`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();
      if (data.success) {
        setShowModal(false);
        loadData();
        const added = data.data?.points_added ?? payload.points ?? previewPoints;
        showSuccess(`${added} ponto(s) adicionados com sucesso!`);
      } else {
        showError(data.message || 'Erro ao adicionar pontos');
      }
    } catch {
      showError('Erro de conexão');
    }
  };

  const formatCPF = (cpf: string) => {
    return cpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  };

  // Calcular estatísticas
  const totalEarned = transactions
    .filter(t => t.type === 'earned')
    .reduce((sum, t) => sum + t.points, 0);
  
  const totalRedeemed = transactions
    .filter(t => t.type === 'redeemed')
    .reduce((sum, t) => sum + t.points, 0);

  const totalCustomersWithPoints = customers.filter(c => c.total_points > 0).length;

  return (
    <Container>
      <Header>
        <HeaderLeft>
          <BackButton onClick={() => navigate('/dashboard')}>
            <ArrowLeft size={16} />
            Voltar
          </BackButton>
          <Title>Gestão de Pontos</Title>
        </HeaderLeft>
        <Actions>
          <ConfigButton onClick={handleOpenConfig}>
            <Settings size={16} />
            Configurar Pontos
          </ConfigButton>
          <AddButton onClick={handleAddPoints}>
            <Plus size={16} />
            Adicionar Pontos
          </AddButton>
        </Actions>
      </Header>

      <StatsRow>
        <StatCard>
          <StatIcon color="#28a745">
            <TrendingUp size={24} />
          </StatIcon>
          <StatValue>{totalEarned.toLocaleString()}</StatValue>
          <StatLabel>Total de Pontos Concedidos</StatLabel>
        </StatCard>
        <StatCard>
          <StatIcon color="#dc3545">
            <TrendingDown size={24} />
          </StatIcon>
          <StatValue>{totalRedeemed.toLocaleString()}</StatValue>
          <StatLabel>Total de Pontos Resgatados</StatLabel>
        </StatCard>
        <StatCard>
          <StatIcon color="#00d4aa">
            <Users size={24} />
          </StatIcon>
          <StatValue>{totalCustomersWithPoints}</StatValue>
          <StatLabel>Clientes com Pontos</StatLabel>
        </StatCard>
        <StatCard>
          <StatIcon color="#ffc107">
            <History size={24} />
          </StatIcon>
          <StatValue>{transactions.length}</StatValue>
          <StatLabel>Total de Transações</StatLabel>
        </StatCard>
      </StatsRow>

      <ContentArea>
        <TabsContainer>
          <Tab 
            active={activeTab === 'transactions'} 
            onClick={() => setActiveTab('transactions')}
          >
            Histórico de Transações
          </Tab>
          <Tab 
            active={activeTab === 'ranking'} 
            onClick={() => setActiveTab('ranking')}
          >
            Ranking de Clientes
          </Tab>
        </TabsContainer>

        <TabContent>
          {loading ? (
            <LoadingContainer>
              Carregando dados...
            </LoadingContainer>
          ) : activeTab === 'transactions' ? (
            <TransactionList>
              {transactions.length === 0 ? (
                <EmptyState>
                  Nenhuma transação encontrada.
                </EmptyState>
              ) : (
                transactions.map((transaction) => (
                  <TransactionItem key={transaction.id} className={transaction.type}>
                    <TransactionInfo>
                      <TransactionCustomer>
                        {transaction.customer.name}
                      </TransactionCustomer>
                      <TransactionDescription>
                        {transaction.description}
                      </TransactionDescription>
                      <TransactionDate>
                        <Calendar size={12} style={{ marginRight: '4px' }} />
                        {new Date(transaction.created_at).toLocaleString('pt-BR')}
                      </TransactionDate>
                    </TransactionInfo>
                    <TransactionPoints type={transaction.type}>
                      {transaction.type === 'earned' ? '+' : '-'}
                      {transaction.points}
                      <Star size={16} />
                    </TransactionPoints>
                  </TransactionItem>
                ))
              )}
            </TransactionList>
          ) : (
            <TransactionList>
              {customers
                .filter(c => c.total_points > 0)
                .sort((a, b) => b.total_points - a.total_points)
                .map((customer, index) => (
                  <TransactionItem key={customer.id}>
                    <TransactionInfo>
                      <TransactionCustomer>
                        #{index + 1} - {customer.name}
                      </TransactionCustomer>
                      <TransactionDescription>
                        CPF: {formatCPF(customer.cpf)}
                      </TransactionDescription>
                      <TransactionDescription>
                        {customer.email}
                      </TransactionDescription>
                    </TransactionInfo>
                    <TransactionPoints type="earned">
                      {customer.total_points}
                      <Star size={16} />
                    </TransactionPoints>
                  </TransactionItem>
                ))}
            </TransactionList>
          )}
        </TabContent>
      </ContentArea>

      {/* Modal: Configurar Pontos */}
      <Modal $show={showConfigModal}>
        <ModalContent>
          <ModalTitle>Configurar Pontos</ModalTitle>
          <form onSubmit={handleSaveConfig}>
            <FormGroup>
              <Label>Pontos por R$ 1,00</Label>
              <Input
                type="number"
                step="0.0001"
                min="0.0001"
                max="1000"
                value={configDraft.points_per_currency}
                onChange={(e) => setConfigDraft({ ...configDraft, points_per_currency: parseFloat(e.target.value) || 1 })}
                required
              />
            </FormGroup>
            <FormGroup>
              <Label>Valor mínimo de compra (R$) — opcional</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={configDraft.min_purchase_amount ?? ''}
                onChange={(e) => setConfigDraft({ ...configDraft, min_purchase_amount: e.target.value ? parseFloat(e.target.value) : null })}
                placeholder="Sem mínimo"
              />
            </FormGroup>
            <FormGroup>
              <Label>Arredondamento de pontos</Label>
              <SelectInput
                value={configDraft.points_rounding}
                onChange={(e) => setConfigDraft({ ...configDraft, points_rounding: e.target.value as StoreConfig['points_rounding'] })}
              >
                <option value="round">Arredondar (padrão)</option>
                <option value="floor">Truncar (para baixo)</option>
                <option value="ceil">Arredondar para cima</option>
              </SelectInput>
            </FormGroup>
            <ModalActions>
              <ModalButton type="button" onClick={() => setShowConfigModal(false)}>
                Cancelar
              </ModalButton>
              <ModalButton type="submit" variant="primary" disabled={isSavingConfig}>
                {isSavingConfig ? 'Salvando...' : 'Salvar'}
              </ModalButton>
            </ModalActions>
          </form>
        </ModalContent>
      </Modal>

      <Modal $show={showModal}>
        <ModalContent>
          <ModalTitle>Adicionar Pontos</ModalTitle>
          <form onSubmit={handleSubmit}>
            <FormGroup>
              <Label>Cliente</Label>
              <Select
                value={formData.customer_id}
                onChange={(e) => setFormData({ ...formData, customer_id: e.target.value })}
                required
              >
                <option value="">Selecione um cliente</option>
                {customers.map((customer) => (
                  <option key={customer.id} value={customer.id}>
                    {customer.name} - {formatCPF(customer.cpf)}
                  </option>
                ))}
              </Select>
            </FormGroup>

            {!manualMode ? (
              <>
                <InfoBanner>
                  1 real = {storeConfig.points_per_currency} ponto(s)
                  {storeConfig.min_purchase_amount !== null &&
                    ` • Mínimo: R$ ${storeConfig.min_purchase_amount.toFixed(2).replace('.', ',')}`}
                </InfoBanner>
                <FormGroup>
                  <Label>Valor da Compra (R$)</Label>
                  <Input
                    type="number"
                    value={formData.purchase_amount}
                    onChange={(e) => setFormData({ ...formData, purchase_amount: e.target.value })}
                    placeholder="50,00"
                    min="0"
                    step="0.01"
                    required
                  />
                  {formData.purchase_amount && (
                    <PointsPreview $valid={previewPoints !== null && previewPoints >= 0 && previewPoints !== -1}>
                      {previewPoints === -1
                        ? `Abaixo do valor mínimo`
                        : previewPoints !== null
                          ? `= ${previewPoints} ponto(s)`
                          : ''}
                    </PointsPreview>
                  )}
                  <ToggleLink type="button" onClick={() => setManualMode(true)}>
                    Inserir pontos manualmente
                  </ToggleLink>
                </FormGroup>
              </>
            ) : (
              <FormGroup>
                <Label>Quantidade de Pontos</Label>
                <Input
                  type="number"
                  value={formData.points}
                  onChange={(e) => setFormData({ ...formData, points: e.target.value })}
                  placeholder="100"
                  min="1"
                  required
                />
                <ToggleLink type="button" onClick={() => setManualMode(false)}>
                  Usar valor de compra
                </ToggleLink>
              </FormGroup>
            )}

            <FormGroup>
              <Label>Descrição</Label>
              <Input
                type="text"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Compra realizada"
                required
              />
            </FormGroup>
            <ModalActions>
              <ModalButton type="button" onClick={() => setShowModal(false)}>
                Cancelar
              </ModalButton>
              <ModalButton
                type="submit"
                variant="primary"
                disabled={!manualMode && (previewPoints === null || previewPoints <= 0)}
              >
                Adicionar Pontos
              </ModalButton>
            </ModalActions>
          </form>
        </ModalContent>
      </Modal>
    </Container>
  );
};
